// A step's video in Vimeo's own player (@vimeo/player, bundled, never a CDN): Pick your patch's
// episodes, and any Start here step that is a video. Until Play is pressed it's the shared player's
// black box (src/product/Player.jsx's look), so nothing loads from Vimeo for someone who only looks;
// a step with no video link yet says so and has no Play button.
//
// Where they are is saved as PCFL v3 saves it: every 15 seconds while it plays, on pause (the start
// again at the very end), and as the page closes; and the first time it passes the product's watched
// share, the step is ticked done. The screen does the saving (onPlace, onDone), and never while staff
// view as a member.
//
//   <StepPlayer title url savedSeconds watched watchedShare onPlace={s => ...} onDone={() => ...} phone />
import { useEffect, useRef, useState } from 'react'
import Player from '@vimeo/player'
import * as f from '../../lib/format.js'
import { parseVimeo, vimeoSrc, positionOnPause, SAVE_EVERY_MS } from '../../free/vimeo.js'
import { PlayFill, Tick } from '../../product/Icons.jsx'
import { COPY } from './copy.js'

export function StepPlayer({ title, url, savedSeconds = 0, watched = false, watchedShare = 0.9, onPlace, onDone, phone = false }) {
  const vimeo = parseVimeo(url)
  const [start, setStart] = useState(null)
  const frame = useRef(null)
  const cb = useRef({})
  cb.current = { onPlace, onDone }

  const loaded = start != null && Boolean(vimeo)
  useEffect(() => {
    if (!loaded || !frame.current) return undefined
    const p = new Player(frame.current)
    let lastSave = Date.now()
    let last = start
    let done = watched
    const place = s => cb.current.onPlace?.(Math.max(0, Math.floor(s)))
    const onTime = e => {
      last = e.seconds
      if (!done && e.percent >= watchedShare) {
        done = true
        lastSave = Date.now()
        cb.current.onDone?.()
        place(e.seconds)
        return
      }
      if (Date.now() - lastSave >= SAVE_EVERY_MS) { lastSave = Date.now(); place(e.seconds) }
    }
    const onPause = e => place(positionOnPause(e.seconds, e.percent))
    const onEnd = () => { if (!done) { done = true; cb.current.onDone?.() } place(0) }
    const onLeave = () => place(last)
    p.on('timeupdate', onTime)
    p.on('pause', onPause)
    p.on('ended', onEnd)
    window.addEventListener('pagehide', onLeave)
    return () => {
      window.removeEventListener('pagehide', onLeave)
      try { p.off('timeupdate', onTime); p.off('pause', onPause); p.off('ended', onEnd) } catch { /* the frame is going */ }
      place(last)
    }
  }, [loaded])

  if (loaded) {
    const src = vimeoSrc(vimeo, { autoplay: true }) + (start > 0 ? `#t=${start}s` : '')
    return (
      <div className="pp-player pp-player--on od-player">
        <iframe ref={frame} src={src} title={title} allow="autoplay; fullscreen; picture-in-picture; encrypted-media" allowFullScreen />
      </div>
    )
  }
  const resume = !watched && savedSeconds > 10
  const line = !vimeo ? COPY.videoNotUp : resume ? COPY.carryOnFrom(f.clock(savedSeconds)) : COPY.pressPlay
  return (
    <div className={vimeo ? 'pp-player od-player' : 'pp-player od-player od-player--none'}>
      {vimeo && (
        <button type="button" className="pp-play" aria-label={`Play ${title}`} onClick={() => setStart(resume ? savedSeconds : 0)}>
          <PlayFill size={phone ? 26 : 34} />
        </button>
      )}
      <div className="pp-player-foot">
        <span className="pp-player-line">
          <span role={vimeo ? undefined : 'status'}>{line}</span>
          {watched && <span className="pp-player-watched"><Tick />{COPY.watched}</span>}
        </span>
      </div>
    </div>
  )
}
